import { useEffect, useRef, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { VavClient, linkVerificationCode, offerLink, type LinkOffer } from '@vav/client-core';
import { LanguageDialog } from './LanguageDialog.tsx';
import { PhoneField } from './PhoneField.tsx';
import { validateUsername } from '@vav/protocol';
import { Wordmark } from './Wordmark.tsx';
import { useT } from '../i18n.tsx';
import { createStore } from '../idbStore.ts';

interface Props {
  onRegister: (username: string, displayName: string, phone?: string) => Promise<void>;
  error?: string;
  /** Set when the user arrived through an invite link. */
  inviter?: { displayName: string; username: string };
  /** For the linking flow, which needs a client before there is an account. */
  serverUrl: string;
  profileId: string;
  /** A scanned approval landed in a browser that holds no account. */
  notice?: string;
}

export function Onboarding({ onRegister, error, inviter, serverUrl, profileId, notice }: Props) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const t = useT();

  /**
   * Linking this device to an existing account, instead of creating one.
   *
   * This side only displays and waits: it mints an ephemeral key, shows it as
   * a QR and a copyable code, and polls until the account's existing device
   * approves. The six digits exist for the human check — the approving side
   * shows the same digits derived from what it scanned, and matching them is
   * what defeats a swapped QR.
   */
  const [linking, setLinking] = useState(false);
  const [offer, setOffer] = useState<LinkOffer>();
  const [qr, setQr] = useState<string>();
  const [linkFailed, setLinkFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef({ aborted: false });

  async function startLinking() {
    setLinking(true);
    setLinkFailed(false);
    abortRef.current = { aborted: false };
    try {
      const client = new VavClient({ serverUrl, store: await createStore(profileId) });
      await client.load();
      const fresh = await client.beginLinking();
      setOffer(fresh);
      // An https link, not the raw vav:// code: a phone's native camera
      // opens it straight into the app's approval screen. A vav:// QR gets
      // "no app can open this" instead — the first field report.
      setQr(await QRCode.toDataURL(offerLink(window.location.origin, fresh.code), { margin: 1, width: 220 }));
      await client.completeLinking(fresh, abortRef.current);
      // The account now lives in this profile's store; boot into it.
      window.location.reload();
    } catch {
      if (!abortRef.current.aborted) setLinkFailed(true);
      setOffer(undefined);
      setQr(undefined);
    }
  }

  function cancelLinking() {
    abortRef.current.aborted = true;
    setLinking(false);
    setOffer(undefined);
    setQr(undefined);
    setLinkFailed(false);
  }

  useEffect(() => () => {
    abortRef.current.aborted = true;
  }, []);

  if (linking) {
    return (
      <div className="onboarding">
        <div className="card onboarding__card">
          <h2>{t('onboarding.link.title')}</h2>
          <p className="muted">{t('onboarding.link.hint')}</p>

          {qr && offer ? (
            <>
              <img className="link__qr" src={qr} alt={t('onboarding.link.title')} />
              <p className="link__verify" dir="ltr">
                {linkVerificationCode(offer.ephemeral.pub)}
              </p>
              <p className="hint">{t('onboarding.link.verifyHint')}</p>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(offerLink(window.location.origin, offer.code))
                    .then(() => setCopied(true));
                }}
              >
                {copied ? t('onboarding.link.copied') : t('onboarding.link.copy')}
              </button>
              <p className="hint">{t('onboarding.link.waiting')}</p>
            </>
          ) : linkFailed ? (
            <p className="error">{t('onboarding.link.failed')}</p>
          ) : (
            <p className="muted">…</p>
          )}

          <button type="button" className="ghost" onClick={cancelLinking}>
            {t('profile.name.cancel')}
          </button>
        </div>
      </div>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onRegister(username.trim(), displayName.trim() || username.trim(), phone.trim());
    } finally {
      setBusy(false);
    }
  }

  // Shared with the server rather than a second regex here -- two independent
  // spellings of "what a username is" drift, and the drift shows up as a form
  // that accepts a name the server then rejects.
  const problem = username.trim() ? validateUsername(username) : 'too_short';
  const valid = problem === undefined;

  return (
    <div className="onboarding">
      <form className="card onboarding__card" onSubmit={submit}>
        {/*
          The tagline is justified to the exact width of the wordmark. Both take
          their width from --wordmark-width so they cannot drift apart; the
          tagline stretches by letter-spacing, never by changing font size.

          It stays in English in every locale. It is part of the mark, like the
          wordmark itself -- not UI copy. Never route it through the translation
          catalogue. `dir="ltr"` is required because the document is RTL: without
          it the full stops migrate to the wrong end of the line.
        */}
        <div className="onboarding__brand">
          <h1 className="onboarding__wordmark">
            <Wordmark />
            <span className="visuallyHidden">VAV</span>
          </h1>
          <p className="onboarding__tagline" dir="ltr">
            TWO ENDS. NO MIDDLE.
          </p>
        </div>

        {notice && <p className="inviteBanner">{notice}</p>}
        {inviter && (
          <p className="inviteBanner">
            {/* The name goes through the message, not around it: in some
                languages it belongs at the other end of the sentence. */}
            {t('onboarding.invited', { name: inviter.displayName })}
            <br />
            <small>{t('onboarding.invited.detail')}</small>
          </p>
        )}
        <p className="muted">{t('onboarding.intro')}</p>

        <label htmlFor="username">{t('onboarding.username.label')}</label>
        <input
          id="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="dana"
          autoComplete="off"
          autoFocus
          dir="auto"
        />
        {/* The specific reason, not a generic "invalid" -- "mixes two writing
            systems" is actionable and "invalid username" is not. */}
        <p className={problem && username.trim() ? 'hint error' : 'hint'}>
          {problem && username.trim()
            ? t(`onboarding.username.error.${problem}` as const)
            : t('onboarding.username.hint')}
        </p>

        <label htmlFor="displayName">{t('onboarding.displayName.label')}</label>
        <input
          id="displayName"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder=""
          autoComplete="off"
          dir="auto"
        />

        {/*
          Optional, and it has to read that way without being buried. The brief
          is explicit that a benefits-only pitch is a dark pattern, so the hint
          states the limits too: not the account name, not a credential,
          removable on its own. Nothing here gates the submit button.
        */}
        <PhoneField label={t('onboarding.phone.label')} onChange={setPhone} />
        <p className="hint">{t('onboarding.phone.hint')}</p>

        {error && <p className="error">{error}</p>}

        <button type="submit" className="primary" disabled={!valid || busy}>
          {busy ? t('onboarding.submit.busy') : t('onboarding.submit')}
        </button>

        <p className="hint onboarding__warning">{t('onboarding.warning')}</p>

        {/* The other door: this device joins an account that already exists. */}
        <button type="button" className="ghost" onClick={() => void startLinking()}>
          🔗 {t('onboarding.link.start')}
        </button>

        {/*
          The picker has to be reachable from here, not only from the sidebar
          behind registration. Someone who lands in a language they cannot read
          needs to change it *before* being asked to choose a username, not
          after — otherwise the one screen they must complete is the one screen
          they cannot use.
        */}
        <button
          type="button"
          className="ghost onboarding__language"
          onClick={() => setLanguageOpen(true)}
        >
          🌐 {t('language.title')}
        </button>
      </form>

      {languageOpen && <LanguageDialog onClose={() => setLanguageOpen(false)} />}
    </div>
  );
}
